// src/adm/pages/UserTimeLog.jsx
import { useEffect, useMemo, useState } from "react";
import AdmNav from "../components/AdmNav";
import { AdmBranchAPI, AdmTimeLog } from "../api"; // ✅ 시간별 호출은 api.js의 AdmTimeLog 사용

/** 오늘 (YYYY-MM-DD) */
function getToday() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function rangeHourLabels() {
  return Array.from({ length: 24 }, (_, i) => String(i).padStart(2, "0"));
}

function BranchSelect({ options, value, onChange }) {
  const grouped = useMemo(() => {
    const list = [];
    let currentArea = "";
    options.forEach((b) => {
      if (b.branch_area !== currentArea) {
        currentArea = b.branch_area;
        list.push({ type: "header", text: `-----${currentArea}-----` });
      }
      list.push({
        type: "item",
        value: String(b.branch_idx),
        text: b.branch_name,
      });
    });
    return list;
  }, [options]);

  return (
    <select id="branch" className="" value={value} onChange={(e) => onChange(e.target.value)}>
      <option value="">지점선택</option>
      {grouped.map((opt, i) =>
        opt.type === "header" ? (
          <option key={`h-${i}`} value="" disabled>
            {opt.text}
          </option>
        ) : (
          <option key={opt.value} value={opt.value}>
            {opt.text}
          </option>
        )
      )}
    </select>
  );
}
function normalizeTimeCounts(rowsFromApi, ymd) {
  const labels = rangeHourLabels(); // '00' ~ '23'
  const map = new Map(); // id -> {'00':0, ... , '23':0}

  (Array.isArray(rowsFromApi) ? rowsFromApi : []).forEach((r) => {
    const id = String(r.id ?? "");
    const t = String(r.time ?? "");
    if (!id || t.length < 13) return; // 'YYYY-MM-DD HH' 최소 길이

    const h = t.slice(11, 13);
    if (!labels.includes(h)) return;

    if (!map.has(id)) {
      map.set(
        id,
        labels.reduce((acc, hh) => ((acc[hh] = 0), acc), {})
      );
    }
    const c = Number(r.count ?? r.cnt ?? r.value ?? 0);
    map.get(id)[h] += Number.isFinite(c) ? c : 0;
  });

  const rowsNorm = Array.from(map.entries()).map(([id, hourCounts]) => {
    const rowTotal = labels.reduce((s, hh) => s + (hourCounts[hh] || 0), 0);
    return { id, hourCounts, rowTotal };
  });
  const total = rowsNorm.reduce((s, r) => s + r.rowTotal, 0);
  return { labels, rows: rowsNorm, total, dateText: ymd };
}

export default function UserTimeLog() {
  const [branches, setBranches] = useState([]);
  const [branchIdx, setBranchIdx] = useState("");
  const [date, setDate] = useState(getToday());

  // 결과
  const [headers, setHeaders] = useState(rangeHourLabels());
  const [rows, setRows] = useState([]); 
  const [grandTotal, setGrandTotal] = useState(0);
  const [rangeText, setRangeText] = useState("");

  // UI 상태
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const list = await AdmBranchAPI.list();
        setBranches(Array.isArray(list) ? list : []);
      } catch (e) {
        console.error(e);
        setBranches([]);
      }
    })();
  }, []);

  /** 조회 (✅ 지점 미선택이면 전체 조회) */
  const onSearch = async () => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) {
      alert("조회일을 선택하세요.");
      return;
    }

    try {
      setLoading(true);
      setErr("");

      const resRows = await AdmTimeLog.hourCounts({
        branch_idx: branchIdx || undefined, // 지점 미선택 시 undefined → 전체 조회
        date, // YYYY-MM-DD
      });

      const { labels, rows, total, dateText } = normalizeTimeCounts(resRows, date);

      setHeaders(labels);
      setRows(rows.sort((a, b) => String(a.id).localeCompare(String(b.id))));
      setGrandTotal(total);
      setRangeText(`${dateText} 총 사용량: ${total}`);
    } catch (e) {
      console.error(e);
      setErr(e?.message || "조회 실패");
      setHeaders(rangeHourLabels());
      setRows([]);
      setGrandTotal(0);
      setRangeText("");
    } finally {
      setLoading(false);
    }
  };

  // 시간대 총계
  const hourTotals = useMemo(() => {
    if (!headers.length || !rows.length) return Array(24).fill(0);
    return headers.map((hh) => rows.reduce((s, r) => s + (r.hourCounts?.[hh] || 0), 0));
  }, [headers, rows]);

  // 날짜 인풋 클릭 시 피커 강제 오픈 (UserDayLog와 동일 UX)
  const handleDateMouseDown = (e) => {
    const el = e.currentTarget;
    try {
      if (typeof el.showPicker === "function") el.showPicker();
    } catch {
      el.focus();
    }
  };

  return (
    <>
      <AdmNav />
      <section className="adm-branch adm-UserTimeLog">
        <div className="adm-card">
          <div className="adm-title-row">
            <h2>API 시간별 사용량 조회</h2>
          </div>

          <div className="adm-toolbar">
            <div className="datafilterbox">
              <label className="">회사 :</label>
              <BranchSelect options={branches} value={branchIdx} onChange={setBranchIdx} />
              <label className="">조회일 :</label>
              <input type="date" className="adm-date" value={date} onChange={(e) => setDate(e.target.value)} onMouseDown={handleDateMouseDown} />
            </div>
            <button className="btn" onClick={onSearch}>
              조회
            </button>
          </div>

          {/* 기간/총합 Summary */}
          {rangeText && <div className="total-data">{rangeText}</div>}

          <div className="adm-table-wrap">
            <table className="adm-table" cellPadding={0} cellSpacing={0}>
              <thead>
                <tr className="table-header">
                  <th>아이디</th>
                  {headers.map((hh) => (
                    <th key={hh}>{hh}시</th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  <tr>
                    <td className="center" colSpan={Math.max(2, headers.length + 1)}>
                      로딩 중…
                    </td>
                  </tr>
                ) : err ? (
                  <tr>
                    <td className="center error" colSpan={Math.max(2, headers.length + 1)}>
                      {err}
                    </td>
                  </tr>
                ) : rows.length === 0 ? (
                  <tr>
                    <td className="center" colSpan={Math.max(2, headers.length + 1)}>
                      결과가 없습니다.
                    </td>
                  </tr>
                ) : (
                  rows.map((r) => (
                    <tr key={r.id}>
                      <td className="p-3">{r.id}</td>
                      {headers.map((hh) => {
                        const v = Number(r.hourCounts?.[hh] ?? 0);
                        return (
                          <td key={hh} className="p-3">
                            {v > 0 ? <span className="time-val">{v}</span> : `-`}
                          </td>
                        );
                      })}
                    </tr>
                  ))
                )}
              </tbody>

              {/* 총계 행 */}
              {headers.length > 0 && (
                <tfoot>
                  <tr className="table-footer">
                    <th>총계</th>
                    {hourTotals.map((n, i) => {
                      const v = Number(n || 0);
                      return <th key={i}>{v > 0 ? <span className="time-val">{v}</span> : `-`}</th>;
                    })}
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>
      </section>
    </>
  );
}
